import { actionCreator } from '@ardoq/rxbeach';
import type { Trackable } from 'tracking/trackingData';
import {
  APIComponentAttributes,
  APIFieldAttributes,
  APIModelAttributes,
  APIReferenceAttributes,
  APITagAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  ComponentSelection,
  CreatedItemsLoadedState,
  LoadedState,
  LoadedStateHash,
  PathCollapsingRule,
  WithTransactionId,
} from '@ardoq/api-types';

export type RegisterLoadedStatePayload = {
  loadedStates: LoadedState[];
  viewName?: string;
};

export const registerLoadedState = actionCreator<RegisterLoadedStatePayload>(
  '[loadedState] REGISTER_LOADED_STATE'
);

export const clearLoadedState = actionCreator(
  '[loadedState] CLEAR_LOADED_STATE'
);

export type SetTraversalIdPayload = {
  id: ArdoqId;
  loadedStateHash: LoadedStateHash;
};

export const setTraversalId = actionCreator<SetTraversalIdPayload>(
  '[loadedState] SET_TRAVERSAL_ID'
);

export type ToggleLoadedStateVisibilityPayload = {
  loadedStateHash: LoadedStateHash;
};

export const toggleLoadedStateVisibility =
  actionCreator<ToggleLoadedStateVisibilityPayload>(
    '[loadedState] SET_IS_HIDDEN'
  );

export const deleteLoadedState = actionCreator<string>(
  '[loadedState] DELETE_LOADED_STATE'
);

export type RequestRemoveLoadedStatePayload = {
  loadedStateHash: LoadedStateHash;
  loadedStatesCount: number;
  /**
   * Whether the dataset has a traversal definition the user would lose by
   * closing it. A traversal without paths has nothing to lose, so it is treated
   * like a plain component set.
   */
  hasTraversalDefinition: boolean;
  isSaved: boolean;
};

export const requestRemoveLoadedState = actionCreator<
  Trackable<RequestRemoveLoadedStatePayload>
>('[loadedState] REQUEST_REMOVE_LOADED_STATE');

export type LoadedStateUpdatedPayload =
  | {
      existingHash: string;
      loadedState: LoadedState;
      isMainLoadedState?: false;
    }
  | {
      existingHash: '';
      loadedState: LoadedState;
      isMainLoadedState: true;
    };

/**
 * A loadedState's hash is based on its params, so when the params are modified
 * the hash changes and the loadedState has to be replaced atomically.
 */
export const loadedStateUpdated = actionCreator<LoadedStateUpdatedPayload>(
  '[loadedState] LOADED_STATE_UPDATED'
);

type SetTraversalContextComponentPayload = {
  loadedStateHash: LoadedStateHash;
  componentSelection: ComponentSelection;
};

export const setTraversalContextComponent =
  actionCreator<SetTraversalContextComponentPayload>(
    '[loadedState] SET_CONTEXT_COMPONENT'
  );

export type ToggleCollapsedPathActivePayload = {
  rule: PathCollapsingRule;
  loadedStateHash: LoadedStateHash;
};

export const toggleCollapsedPathActive =
  actionCreator<ToggleCollapsedPathActivePayload>(
    '[loadedState] TOGGLE_COLLAPSED_PATH_ACTIVE'
  );

export const itemsCreated = actionCreator<CreatedItemsLoadedState>(
  '[loadedState] ITEMS_CREATED'
);

export type CreatedItemsProcessedPayload = {
  componentIds: ArdoqId[];
  referenceIds: ArdoqId[];
};

export const createdItemsProcessed =
  actionCreator<CreatedItemsProcessedPayload>(
    '[loadedState] ITEMS_CREATED_PROCESSED'
  );

export type ClearAndSetLoadedStatePayload = WithTransactionId & {
  loadedStates: LoadedState[];
  viewName?: string;
};

export const clearAndSetLoadedState =
  actionCreator<ClearAndSetLoadedStatePayload>(
    '[loadedState] CLEAR_AND_SET_LOADED_STATE'
  );

export type EntityPatch<T extends { _id: ArdoqId }> = {
  created?: Record<ArdoqId, T>;
  updated?: Record<ArdoqId, T>;
  deletedIds?: ArdoqId[];
};

// Sparse per-collection delta for a single loaded state's scopeData. Each
// entity-type key is optional so a routine only fills the collections it
// touches.
export type LoadedStateScopeDataPatch = {
  components?: EntityPatch<APIComponentAttributes>;
  references?: EntityPatch<APIReferenceAttributes>;
  workspaces?: EntityPatch<APIWorkspaceAttributes>;
  models?: EntityPatch<APIModelAttributes>;
  fields?: EntityPatch<APIFieldAttributes>;
  tags?: EntityPatch<APITagAttributes>;
};

export type LoadedStatesScopeDataPatchedPayload = {
  partials: Record<LoadedStateHash, LoadedStateScopeDataPatch>;
};

export const loadedStatesScopeDataPatched =
  actionCreator<LoadedStatesScopeDataPatchedPayload>(
    '[loadedState] SCOPE_DATA_PATCHED'
  );
